import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMingo } from '../context/MingoContext';
import { fetchMyAppUserRow } from '../api/profileApi';
import { getVisaBannerDismissDay, setVisaBannerDismissDay } from '../lib/visaStorage';
import { daysLeftFromYmd, toYmdDateOnly } from '../lib/visaDateUrgency';

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

const LS_URGENT = 'mingo_visa_urgent_snooze';

function VisaExpiryOverlay() {
  const navigate = useNavigate();
  const { t, isGuest } = useMingo();
  const [tier, setTier] = useState(null); // 'd30' | 'd7' | 'expired' | null
  const [urgent, setUrgent] = useState(false);
  const [dismissedToday, setDismissedToday] = useState(() => getVisaBannerDismissDay() === todayKey());

  const load = useCallback(async () => {
    if (isGuest) {
      setTier(null);
      setUrgent(false);
      return;
    }
    const me = await fetchMyAppUserRow();
    if (!me?.visa_expire_date) {
      setTier(null);
      setUrgent(false);
      return;
    }
    const d = daysLeftFromYmd(toYmdDateOnly(me.visa_expire_date));
    if (d == null) {
      setTier(null);
      setUrgent(false);
      return;
    }
    if (d < 0) {
      setTier('expired');
      setUrgent(false);
      return;
    }
    if (d <= 1) {
      setTier(null);
      if (typeof window !== 'undefined' && window.localStorage.getItem(LS_URGENT) === todayKey()) {
        setUrgent(false);
      } else {
        setUrgent(true);
      }
      return;
    }
    if (d <= 7) {
      setTier('d7');
      setUrgent(false);
      return;
    }
    if (d <= 30) {
      setTier('d30');
      setUrgent(false);
      return;
    }
    setTier(null);
    setUrgent(false);
  }, [isGuest]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onVisa = () => void load();
    window.addEventListener('mingo-visa-updated', onVisa);
    return () => window.removeEventListener('mingo-visa-updated', onVisa);
  }, [load]);

  const goVisa = () => {
    navigate('/visa-info');
  };

  const dismissBanner = () => {
    setVisaBannerDismissDay(todayKey());
    setDismissedToday(true);
  };

  if (urgent) {
    return (
      <div className="mingo-modal-root mingo-visa-urgent-root" role="presentation">
        <button
          type="button"
          className="mingo-modal-backdrop"
          aria-label={t('common.close')}
          onClick={() => {
            window.localStorage.setItem(LS_URGENT, todayKey());
            setUrgent(false);
          }}
        />
        <div className="mingo-modal-dialog mingo-visa-urgent" role="dialog" aria-modal="true">
          <h2 className="mingo-modal-dialog__title">{t('visaAlert.urgentTitle')}</h2>
          <p className="mingo-modal-dialog__body">{t('visaAlert.urgentBody')}</p>
          <div className="mingo-modal-dialog__actions">
            <button type="button" className="mingo-modal-dialog__btn mingo-modal-dialog__btn--primary" onClick={goVisa}>
              {t('visaAlert.goVisa')}
            </button>
            <button
              type="button"
              className="mingo-modal-dialog__btn mingo-modal-dialog__btn--secondary"
              onClick={() => {
                window.localStorage.setItem(LS_URGENT, todayKey());
                setUrgent(false);
              }}
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!tier || dismissedToday) return null;

  const msg =
    tier === 'd30' ? t('visaAlert.banner30') : tier === 'd7' ? t('visaAlert.banner7') : t('visaAlert.bannerExpired');

  return (
    <div className={`mingo-visa-banner mingo-visa-banner--${tier}`} role="alert">
      <button type="button" className="mingo-visa-banner__main" onClick={goVisa}>
        {msg}
      </button>
      <button type="button" className="mingo-visa-banner__x" aria-label={t('visaAlert.dismissToday')} onClick={dismissBanner}>
        ×
      </button>
    </div>
  );
}

export default VisaExpiryOverlay;
